import {
  fetchTabs,
  getCollectionCards,
} from "@/lib/storyblok/storyblokServerFunctions";

import { rpc } from "#rpc";
import { AccordionBlok, AccordionItemBlok } from "@/bloks/AccordionBlok";
import { Container } from "@/bloks/Container";
import { CorrectionList, fetchCorrections } from "@/bloks/Corrections";
import { DealerListings, fetchDealerListings } from "@/bloks/DealerListings";
import Tag from "@/bloks/Tag/Tag";
import TextBanner from "@/bloks/TextBanner";
import SBLink from "@/bloks/atoms/SBLink";
import { ArticleCardBlok } from "../../../../bloks/ArticleCard/ArticleCard";
import ArticleListing from "../../../../bloks/ArticleListingBlok/ArticleListing";
import fetchArticleListing from "../../../../bloks/ArticleListingBlok/fetchArticleListing";
import ArticleListingPage from "../../../../bloks/ArticleListingPage/ArticleListingPage";
import ArticlePage from "../../../../bloks/ArticlePage/ArticlePage";
import Bundle from "../../../../bloks/Bundle";
import CardGrid from "../../../../bloks/CardGrid/CardGrid";
import { CollectionCardBlok } from "../../../../bloks/CollectionCard/CollectionCard";
import CollectionListingPage from "../../../../bloks/CollectionListingPage/CollectionListingPage";
import CollectionPage from "../../../../bloks/CollectionPage/CollectionPage";
import ColoredSection from "../../../../bloks/ColoredSection/ColoredSection";
import Column from "../../../../bloks/Column/Column";
import Columns from "../../../../bloks/Columns/Columns";
import DoubleHero from "../../../../bloks/DoubleHero/DoubleHero";
import FAQs from "../../../../bloks/FAQs/FAQs";
import HeroCard from "../../../../bloks/HeroCard/HeroCard";
import HeroSlider from "../../../../bloks/HeroSlider/HeroSlider";
import Marquee from "../../../../bloks/Marquee/Marquee";
import Media from "../../../../bloks/Media";
import MediaCard from "../../../../bloks/MediaCard/MediaCard";
import MediaGallery from "../../../../bloks/MediaGallery/MediaGallery";
import PageBlok from "../../../../bloks/PageBlok";
import ProductGalleryWithTabs from "../../../../bloks/ProductGalleryWithTabs";
import {
  getBundleCards,
  getTaggedImageCards,
} from "../../../../bloks/ProductGalleryWithTabs/getGalleryCards";
import Quote from "../../../../bloks/Quote/Quote";
import RichText from "../../../../bloks/RichText/RichText";
import TagCloud from "../../../../bloks/TagCloud/TagCloud";
import TaggedImage from "../../../../bloks/TaggedImage";
import Snackbar from "../../../../bloks/statics/Snackbar";
import { fetchFeaturedArticle } from "../../fetchStories";
import { BuildBlokComponents, BuildBlokParameters } from "../typeGenerators";
import { ExtraBlokFields, StoryblokTypes } from "./blok-component-types";

const components: BuildBlokComponents<StoryblokTypes, ExtraBlokFields> = {
  accordionItem: AccordionItemBlok,
  accordion: AccordionBlok,
  Article: ArticlePage,
  ArticleCard: {
    blok: ArticleCardBlok,
    fetcher: rpc.story.bloks.articleCard,
    queryKeyFn: (blok) => blok.article,
  },
  ArticleListing: {
    blok: ArticleListing,
    fetcher: fetchArticleListing,
    queryKeyFn: (blok) => blok.tags,
  },
  articleListingPage: {
    blok: ArticleListingPage,
    fetcher: fetchFeaturedArticle,
    queryKeyFn: (blok) => blok?.featuredArticle,
  },
  bundle: {
    blok: Bundle,
    fetcher: getBundleCards,
    queryKeyFn: (blok) => blok._uid,
  },
  cardGrid: CardGrid,
  CollectionCard: {
    blok: CollectionCardBlok,
    fetcher: rpc.story.bloks.collection,
    queryKeyFn: (blok) => blok.collection,
  },
  collectionListingPage: {
    blok: CollectionListingPage,
    fetcher: getCollectionCards,
    queryKeyFn: (blok) => blok?.collections,
  },
  collectionPage: CollectionPage,
  coloredSection: ColoredSection,
  corrections: {
    blok: CorrectionList,
    fetcher: fetchCorrections,
    queryKeyFn: (blok) => blok._uid,
  },
  column: Column,
  columns: Columns,
  container: Container,
  doubleHero: DoubleHero,
  FAQs: FAQs,
  tag: Tag,
  heroCard: HeroCard,
  link: SBLink,
  heroSlider: HeroSlider,
  marquee: Marquee,
  media: Media,
  mediaCard: MediaCard,
  mediaGallery: MediaGallery,
  page: PageBlok,
  textBanner: TextBanner,
  productGalleryWithTabs: {
    blok: ProductGalleryWithTabs,
    fetcher: fetchTabs,
    queryKeyFn: (blok) => blok.Tabs,
  },
  Quote: Quote,
  richText: RichText,
  snackbar: Snackbar,
  tagCloud: TagCloud,

  taggedImage: {
    blok: TaggedImage,
    fetcher: getTaggedImageCards,
    queryKeyFn: (blok) =>
      blok?.products?.products?.map((p) => p.product) || blok._uid,
  },
  dealerListings: {
    blok: DealerListings,
    fetcher: fetchDealerListings,
    queryKeyFn: (blok) => blok._uid,
  },
  /* We are not rendering anything in this, need for Typescript */
  forhandler: () => null,
};

export const buildGetBlokComponent =
  <
    BlokParameters,
    BlokComponents extends {
      [component in keyof BlokParameters]: any;
    },
  >(
    blokMap: BlokComponents
  ) =>
  (key: keyof BlokParameters) => {
    return blokMap[key] as any;
  };

export const getBlokComponent = buildGetBlokComponent<
  BuildBlokParameters<StoryblokTypes>,
  BuildBlokComponents<StoryblokTypes, ExtraBlokFields>
>(components);

export default components;
